<template>
    <LoadingComponent :props="loading" />
    <div id="shipping-gateway" class="db-tab-div active focus-border-secondary">
        <div class="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-5 bg-white border rounded-md p-3">
            <button @click="selectActive(index)"
                class="db-tab-sub-btn w-full flex items-center gap-3 h-10 px-4 rounded-lg transition bg-white hover:text-primary hover:bg-primary/5"
                :data-tab="'#' + shippingGateway.slug" v-for="(shippingGateway, index) in shippingGateways.slice(0, 3)"
                :key="shippingGateway" :class="index === selectIndex ? 'active' : ''">
                <span class="capitalize whitespace-nowrap text-[15px]">
                    {{ shippingGateway.name }}
                </span>
            </button>

            <div v-if="shippingGateways.length > 3" class="dropdown-group w-full">
                <button
                    class="dropdown-btn w-full flex items-center gap-3 h-10 px-4 rounded-lg transition bg-white hover:text-primary hover:bg-primary/5">
                    <i class="fa-solid fa-circle-chevron-down text-sm"></i>
                    <span class="capitalize whitespace-nowrap text-[15px]"> {{ $t('label.more_gateway') }}</span>
                </button>
                <div class="w-full dropdown-list absolute top-[42px] right-0 z-30 p-2 rounded-md bg-white shadow-lg">
                    <button @click="selectActive(index + 3)"
                        class="w-full flex items-center h-8 px-2 rounded-lg transition bg-white hover:text-primary hover:bg-primary/5"
                        :data-tab="'#' + shippingGateway.slug"
                        v-for="(shippingGateway, index) in shippingGateways.slice(3, shippingGateways.length)"
                        :key="shippingGateway" :class="index + 3 === selectIndex ? 'active' : ''">
                        {{ shippingGateway.name }}
                    </button>
                </div>
            </div>
        </div>
        <div :id="shippingGateway.slug" class="db-card db-tab-sub-div" v-for="(shippingGateway, index) in shippingGateways"
            :key="shippingGateway" :class="{ 'active': index === selectIndex, 'on-update': onUpdateActive && index === selectIndex }">
            <div class="db-card-header flex items-center justify-between p-2 pl-4">
                <h3 class="db-card-title">{{ shippingGateway.name }}</h3>
                <div v-if="isSkydropxGateway(shippingGateway)" class="w-fit">
                    <div class="inline-flex rounded-lg bg-gray-100 p-1 gap-1">
                        <button type="button" class="px-4 h-9 rounded-md text-sm font-medium transition"
                            :class="activeSkydropxTab === 'configuration' ? 'bg-white shadow-sm text-primary' : 'text-gray-600 hover:text-primary'"
                            @click="setSkydropxTab('configuration')">
                            {{ $t('label.shipping_gateway_configuration') }}
                        </button>
                        <button type="button" class="px-4 h-9 rounded-md text-sm font-medium transition"
                            :class="activeSkydropxTab === 'quote_test' ? 'bg-white shadow-sm text-primary' : 'text-gray-600 hover:text-primary'"
                            @click="setSkydropxTab('quote_test')">
                            {{ $t('label.shipping_gateway_quote_test') }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="db-card-body">

                <form @submit.prevent="save(index)" :id="'formElem' + index" class="w-full d-block"
                    v-if="!isSkydropxGateway(shippingGateway) || activeSkydropxTab === 'configuration'">
                    <div class="form-row">
                        <input type="hidden" :value="shippingGateway.slug" name="shipping_type">

                        <template v-if="isSkydropxGateway(shippingGateway)">
                            <div class="form-col-12 mt-2">
                                <h4 class="font-medium text-sm font-client border-b pb-2 mb-2">{{ $t('label.config_state') }}</h4>
                            </div>
                            <div class="form-col-12 sm:form-col-6"
                                v-for="shippingGatewayOption in getSkydropxSectionOptions(shippingGateway, 'api')"
                                :key="shippingGatewayOption.option">
                                <label :for="shippingGatewayOption.option" class="db-field-title" :class="{'required': shouldMarkSkydropxRequired(shippingGateway, shippingGatewayOption)}">
                                    {{ $t("label." + getSkydropxLabelKey(shippingGatewayOption.option)) }}
                                </label>
                                <input v-if="shippingGatewayOption.type === enums.inputTypeEnum.TEXT" type="text"
                                    :value="getSkydropxOptionRenderValue(shippingGateway, shippingGatewayOption)"
                                    @input="onSkydropxOptionInput(shippingGatewayOption.option, $event.target.value)"
                                    v-bind:class="errors[shippingGatewayOption.option] ? 'invalid' : ''"
                                    :name="shippingGatewayOption.option" :id="shippingGatewayOption.option"
                                    class="db-field-control" />

                                <vue-select v-else class="db-field-control"
                                    :modelValue="getGatewaySelectValue(shippingGatewayOption.option, getSkydropxOptionRenderValue(shippingGateway, shippingGatewayOption))"
                                    :options="getGatewayActivityOptions(shippingGatewayOption)"
                                    label-by="label" value-by="id" :closeOnSelect="true" :searchable="true"
                                    :clearOnClose="false" search-placeholder="--"
                                    :placeholder="$t('label.select_one')"
                                    @update:modelValue="onGatewaySelectChange(shippingGatewayOption.option, $event)"
                                    v-bind:class="errors[shippingGatewayOption.option] ? 'invalid' : ''" />
                                <input v-if="shippingGatewayOption.type !== enums.inputTypeEnum.TEXT"
                                    type="hidden" :name="shippingGatewayOption.option"
                                    :value="getGatewaySelectValue(shippingGatewayOption.option, getSkydropxOptionRenderValue(shippingGateway, shippingGatewayOption))" />

                                <small class="db-field-alert" v-if="errors[shippingGatewayOption.option]">{{
                                    errors[shippingGatewayOption.option][0]
                                }}</small>
                            </div>

                            <input v-if="shouldKeepHiddenSkydropxOption(shippingGateway, 'skydropx_api_base_url_production')"
                                type="hidden" name="skydropx_api_base_url_production"
                                :value="getSkydropxHiddenValue(shippingGateway, 'skydropx_api_base_url_production')" />
                            <input v-if="shouldKeepHiddenSkydropxOption(shippingGateway, 'skydropx_api_base_url_sandbox')"
                                type="hidden" name="skydropx_api_base_url_sandbox"
                                :value="getSkydropxHiddenValue(shippingGateway, 'skydropx_api_base_url_sandbox')" />

                            <div class="form-col-12 mt-2">
                                <h4 class="font-medium text-sm font-client border-b pb-2 mb-2">{{ $t('label.skydropx_origin_name') }}</h4>
                            </div>
                            <div class="form-col-12 sm:form-col-6"
                                v-for="shippingGatewayOption in getSkydropxSectionOptions(shippingGateway, 'origin')"
                                :key="shippingGatewayOption.option">
                                <label :for="shippingGatewayOption.option" class="db-field-title" :class="{'required': shouldMarkSkydropxRequired(shippingGateway, shippingGatewayOption)}">
                                    {{ $t("label." + getSkydropxLabelKey(shippingGatewayOption.option)) }}
                                </label>
                                <input v-if="shippingGatewayOption.type === enums.inputTypeEnum.TEXT" type="text"
                                    :value="shippingGatewayOption.value"
                                    v-bind:class="errors[shippingGatewayOption.option] ? 'invalid' : ''"
                                    :name="shippingGatewayOption.option" :id="shippingGatewayOption.option"
                                    class="db-field-control" />

                                <vue-select v-else class="db-field-control"
                                    :modelValue="getGatewaySelectValue(shippingGatewayOption.option, shippingGatewayOption.value)"
                                    :options="getGatewayActivityOptions(shippingGatewayOption)"
                                    label-by="label" value-by="id" :closeOnSelect="true" :searchable="true"
                                    :clearOnClose="false" search-placeholder="--"
                                    :placeholder="$t('label.select_one')"
                                    @update:modelValue="onGatewaySelectChange(shippingGatewayOption.option, $event)"
                                    v-bind:class="errors[shippingGatewayOption.option] ? 'invalid' : ''" />
                                <input v-if="shippingGatewayOption.type !== enums.inputTypeEnum.TEXT"
                                    type="hidden" :name="shippingGatewayOption.option"
                                    :value="getGatewaySelectValue(shippingGatewayOption.option, shippingGatewayOption.value)" />

                                <small class="db-field-alert" v-if="errors[shippingGatewayOption.option]">{{
                                    errors[shippingGatewayOption.option][0]
                                }}</small>
                            </div>

                            <div class="form-col-12 sm:form-col-6"
                                v-for="shippingGatewayOption in getSkydropxRemainingOptions(shippingGateway)"
                                :key="shippingGatewayOption.option">
                                <label :for="shippingGatewayOption.option" class="db-field-title" :class="{'required': shouldMarkSkydropxRequired(shippingGateway, shippingGatewayOption)}">
                                    {{ $t("label." + getSkydropxLabelKey(shippingGatewayOption.option)) }}
                                </label>
                                <input v-if="shippingGatewayOption.type === enums.inputTypeEnum.TEXT" type="text"
                                    :value="shippingGatewayOption.value"
                                    v-bind:class="errors[shippingGatewayOption.option] ? 'invalid' : ''"
                                    :name="shippingGatewayOption.option" :id="shippingGatewayOption.option"
                                    class="db-field-control" />

                                <vue-select v-else class="db-field-control"
                                    :modelValue="getGatewaySelectValue(shippingGatewayOption.option, shippingGatewayOption.value)"
                                    :options="getGatewayActivityOptions(shippingGatewayOption)"
                                    label-by="label" value-by="id" :closeOnSelect="true" :searchable="true"
                                    :clearOnClose="false" search-placeholder="--"
                                    :placeholder="$t('label.select_one')"
                                    @update:modelValue="onGatewaySelectChange(shippingGatewayOption.option, $event)"
                                    v-bind:class="errors[shippingGatewayOption.option] ? 'invalid' : ''" />
                                <input v-if="shippingGatewayOption.type !== enums.inputTypeEnum.TEXT"
                                    type="hidden" :name="shippingGatewayOption.option"
                                    :value="getGatewaySelectValue(shippingGatewayOption.option, shippingGatewayOption.value)" />

                                <small class="db-field-alert" v-if="errors[shippingGatewayOption.option]">{{
                                    errors[shippingGatewayOption.option][0]
                                }}</small>
                            </div>
                        </template>

                        <template v-else>
                            <div class="form-col-12 sm:form-col-6" v-for="shippingGatewayOption in shippingGateway.options"
                                :key="shippingGatewayOption.option">
                                <label :for="shippingGatewayOption.option" class="db-field-title" :class="{'required': shippingGatewayOption.is_required}">
                                    {{ $t("label." + shippingGatewayOption.option) }}
                                </label>
                                <input v-if="shippingGatewayOption.type === enums.inputTypeEnum.TEXT" type="text"
                                    :value="shippingGatewayOption.value"
                                    v-bind:class="errors[shippingGatewayOption.option] ? 'invalid' : ''"
                                    :name="shippingGatewayOption.option" :id="shippingGatewayOption.option"
                                    class="db-field-control" />

                                <vue-select v-else class="db-field-control"
                                    :modelValue="getGatewaySelectValue(shippingGatewayOption.option, shippingGatewayOption.value)"
                                    :options="getGatewayActivityOptions(shippingGatewayOption)"
                                    label-by="label" value-by="id" :closeOnSelect="true" :searchable="true"
                                    :clearOnClose="false" search-placeholder="--"
                                    :placeholder="$t('label.select_one')"
                                    @update:modelValue="onGatewaySelectChange(shippingGatewayOption.option, $event)"
                                    v-bind:class="errors[shippingGatewayOption.option] ? 'invalid' : ''" />
                                <input v-if="shippingGatewayOption.type !== enums.inputTypeEnum.TEXT"
                                    type="hidden" :name="shippingGatewayOption.option"
                                    :value="getGatewaySelectValue(shippingGatewayOption.option, shippingGatewayOption.value)" />

                                <small class="db-field-alert" v-if="errors[shippingGatewayOption.option]">{{
                                    errors[shippingGatewayOption.option][0]
                                }}</small>
                            </div>
                        </template>
                        <div class="form-col-12 border-t pt-5 mt-3">
                            <button type="submit" class="db-btn text-white bg-primary" :disabled="isSaving">
                                <template v-if="isSaving">
                                    <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                        <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                    </svg>
                                    <span>{{ $t('button.updating') }}</span>
                                </template>
                                <template v-else>
                                    <i class="lab lab-fill-save"></i>
                                    <span>{{ $t("button.save") }}</span>
                                </template>
                            </button>
                        </div>
                    </div>
                </form>

                <div v-if="isSkydropxGateway(shippingGateway) && activeSkydropxTab === 'quote_test'" class="form-row">

                    <div class="form-col-12">
						<div class="flex items-center justify-between border-b pb-2 w-full gap-4">
							<h4 class="font-medium text-sm font-client">{{ $t('label.shipping_quote_test') }}</h4>
							<div class="w-fit">
                                    <button type="button" class="db-btn text-white bg-primary"
                                        :class="!canRunQuoteTest() || quoteTest.isCalculating ? 'disabled:opacity-50 disabled:bg-gray' : ''"
                                        :disabled="quoteTest.isCalculating || !canRunQuoteTest()"
									@click="runQuoteTest">
									<span v-if="quoteTest.isCalculating" class="flex items-center gap-2">
                                        <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                            <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                        </svg>
										<span>{{ $t('button.quotating') }}</span>
									</span>
									<span v-else class="flex items-center gap-2">
										<i class="lab lab-fill-save"></i>
										<span>{{ $t('button.run_quote_test') }}</span>
									</span>
								</button>
							</div>
						</div>
                    </div>

                    <div class="form-col-12" v-if="!quoteTest.result && !quoteTest.errorMessage">
                        <div class="text-sm">
                            {{ $t('label.run_quote_info') }}
                        </div>
                    </div>

                    <div class="form-col-12" v-if="quoteTest.errorMessage">
                        <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-600 text-sm">
                            {{ quoteTest.errorMessage }}
                        </div>
                    </div>

                    <div class="form-col-12" v-if="quoteTest.result">
                        <div class="rounded-md border p-4 bg-white">
                            <h4 class="font-semibold text-sm border-b pb-2 mb-3">{{ $t('label.shipping_quote_result') }}</h4>
                            <div class="grid sm:grid-cols-3 gap-3 mb-4">
                                <div class="rounded-md border bg-gray-50 p-3">
                                    <div class="text-xs text-gray-500">{{ $t('label.shipping_charge') }}</div>
                                    <div class="text-lg font-semibold">{{ quoteTest.result.shipping_charge }}</div>
                                </div>
                                <div class="rounded-md border bg-gray-50 p-3">
                                    <div class="text-xs text-gray-500">{{ $t('label.skydropx_rate_mode') }}</div>
                                    <div class="text-lg font-semibold">{{ quoteTest.result.rate_mode }}</div>
                                </div>
                                <div class="rounded-md border bg-gray-50 p-3">
                                    <div class="text-xs text-gray-500">{{ $t('label.select_shipping_quote') }}</div>
                                    <div class="text-lg font-semibold">
                                        {{ quoteTest.result.selected_quote ? quoteTest.result.selected_quote.provider : '-' }}
                                    </div>
                                </div>
                            </div>

                            <div class="db-table-responsive border rounded-md" v-if="quoteTest.result.quotes && quoteTest.result.quotes.length">
                                <table class="db-table">
                                    <thead class="db-table-head border-t-0">
                                        <tr class="db-table-head-tr">
                                            <th class="db-table-head-th">{{ $t('label.provider') }}</th>
                                            <th class="db-table-head-th">{{ $t('label.service') }}</th>
                                            <th class="db-table-head-th">{{ $t('label.days') }}</th>
                                            <th class="db-table-head-th">{{ $t('label.amount') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody class="db-table-body">
                                        <tr class="db-table-body-tr" v-for="quote in quoteTest.result.quotes" :key="quote.id">
                                            <td class="db-table-body-td">{{ quote.provider }}</td>
                                            <td class="db-table-body-td">{{ quote.service_level || '-' }}</td>
                                            <td class="db-table-body-td">{{ quote.days || '-' }}</td>
                                            <td class="db-table-body-td">{{ quote.amount }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div class="form-col-12 mt-2">
                        <h4 class="font-medium text-sm font-client border-b pb-2">{{ $t('label.shipping_address_test') }}</h4>
                    </div>
                    <div class="form-col-12 sm:form-col-4">
                        <label class="db-field-title required">{{ $t('label.shipping_address_id') }}</label>
                        <vue-select class="db-field-control" v-model="quoteTest.shippingAddressId" :options="quoteTestAddressOptions"
                            label-by="label" value-by="id" :closeOnSelect="true" :searchable="true"
                            :clearOnClose="true" :placeholder="$t('label.shipping_address_id_placeholder')"
                            search-placeholder="--" />
                    </div>

                    <div class="form-col-12 mt-2">
                        <h4 class="font-medium text-sm font-client border-b pb-2">{{ $t('label.shipping_product_test') }}</h4>
                    </div>
                    <div class="form-col-12">
                        <div class="product-add rounded-lg border p-4 bg-gray-50">
                            <button type="button" class="w-full flex items-center justify-between"
                                @click="toggleQuoteTestProductAccordion">
                                <h4 class="font-semibold text-sm">{{ $t('label.add_products') }}</h4>
                                <i class="fa-solid fa-chevron-down transition-transform duration-200"
                                    :class="quoteTestProductAccordionExpanded ? 'rotate-180' : ''"></i>
                            </button>
                            <div v-show="quoteTestProductAccordionExpanded" class="grid sm:grid-cols-12 gap-3 items-end border-t pt-3 mt-2">
                                <div class="sm:col-span-7">
                                    <label class="db-field-title required">{{ $t('label.product') }}</label>
                                    <vue-select class="db-field-control" v-model="quoteTest.selectedProductId" :options="simpleProducts"
                                        label-by="name" value-by="id" :closeOnSelect="true" :searchable="true"
                                        :clearOnClose="true" :placeholder="$t('label.select_one')" search-placeholder="--" />
                                </div>
                                <div class="sm:col-span-3">
                                    <label class="db-field-title required">{{ $t('label.quantity') }}</label>
                                    <input type="number" min="1" class="db-field-control" v-model.number="quoteTest.quantity" />
                                </div>
                                <div class="sm:col-span-2">
                                    <button type="button" class="db-btn h-12 w-full font-medium bg-gray-100"
                                        :class="quoteTest.isAddingProduct ? 'disabled:opacity-50' : ''"
                                        :disabled="quoteTest.isAddingProduct"
                                        @click="addQuoteTestProduct">
                                        <span v-if="quoteTest.isAddingProduct" class="flex items-center justify-center gap-2">
                                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                            </svg>
                                            {{ $t('button.adding') }}
                                        </span> 
                                        <span v-else>
                                            <i class="lab lab-line-add-circle"></i>
                                            {{ $t('button.add') }}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="form-col-12" v-if="quoteTest.items.length">
                        <div class="db-table-responsive border rounded-md">
                            <table class="db-table">
                                <thead class="db-table-head border-t-0">
                                    <tr class="db-table-head-tr">
                                        <th class="db-table-head-th"><span class="whitespace-nowrap overflow-hidden text-ellipsis truncate">{{ $t('label.product') }}</span></th>
                                        <th class="db-table-head-th">{{ $t('label.quantity') }}</th>
                                        <th class="db-table-head-th">{{ $t('label.weight_alt') }}</th>
                                        <th class="db-table-head-th">{{ $t('label.length_alt') }}</th>
                                        <th class="db-table-head-th">{{ $t('label.width_alt') }}</th>
                                        <th class="db-table-head-th">{{ $t('label.height_alt') }}</th>
                                        <th class="db-table-head-th text-right">{{ $t('label.action') }}</th>
                                    </tr>
                                </thead>
                                <tbody class="db-table-body">
                                    <tr class="db-table-body-tr" v-for="item in quoteTest.items" :key="item.product_id">
                                        <td class="db-table-body-td">{{ item.name }}</td>
                                        <td class="db-table-body-td">
                                            <input type="number" min="1" class="db-field-control h-8"
                                                v-model.number="item.quantity" @input="normalizeQuoteItemQuantity(item)" />
                                        </td>
                                        <td class="db-table-body-td">{{ item.shipping.weight }} kg</td>
                                        <td class="db-table-body-td">{{ item.shipping.length }} cm</td>
                                        <td class="db-table-body-td">{{ item.shipping.width }} cm</td>
                                        <td class="db-table-body-td">{{ item.shipping.height }} cm</td>
                                        <td class="db-table-body-td text-right">
                                            <button type="button" class="db-btn text-xs h-8 font-medium bg-gray-100 hover:text-danger hover:bg-danger/10"
                                                @click="removeQuoteTestProduct(item.product_id)">
                                                <i class="lab lab-fill-close"></i>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import LoadingComponent from "../../components/LoadingComponent";
import alertService from "../../../../services/alertService";
import inputTypeEnum from "../../../../enums/modules/inputTypeEnum";

export default {
    name: "ShippingGatewayComponent",
    components: { LoadingComponent },
    data() {
        return {
            loading: {
                isActive: false,
            },
            search: {
                paginate: 0,
                order_column: "id",
                order_type: "asc"
            },
            selectIndex: 0,
            enums: {
                inputTypeEnum: inputTypeEnum
            },
            isSaving: false,
            onUpdateActive: false,
            errors: {},
            activeSkydropxTab: "configuration",
            skydropxDraft: {
                sandboxMode: null,
                apiBaseUrlProduction: "",
                apiBaseUrlSandbox: ""
            },
            selectDraft: {},
            quoteTest: {
                shippingAddressId: null,
                selectedProductId: null,
                quantity: 1,
                items: [],
                result: null,
                errorMessage: "",
                isAddingProduct: false,
                isCalculating: false
            },
            quoteTestProductAccordionExpanded: true,
            quoteTestAddressOptions: []

        };
    },
    computed: {
        shippingGateways: function () {
            return this.$store.getters["shippingGateway/lists"];
        },
        simpleProducts: function () {
            return this.$store.getters["product/simpleList"];
        }
    },
    mounted() {
        try {
            this.loading.isActive = true;
            this.$store.dispatch("shippingGateway/lists", this.search).then(() => {
                this.initializeSelectDrafts();
                this.loadSimpleProducts();
                this.loadQuoteTestAddresses();
                this.syncSkydropxFormDrafts();
                this.loading.isActive = false;
            }).catch(() => {
                this.loading.isActive = false;
            });
        } catch (err) {
            this.loading.isActive = false;
            alertService.error(err);
        }
    },
    methods: {
        initializeSelectDrafts: function () {
            this.shippingGateways.forEach((gateway) => {
                gateway.options.forEach((option) => {
                    if (option.type !== this.enums.inputTypeEnum.TEXT) {
                        this.selectDraft[option.option] = option.value;
                    }
                });
            });
        },
        getGatewayActivityOptions: function (shippingGatewayOption) {
            const activities = shippingGatewayOption.activities || {};
            return Object.keys(activities)
                .filter((activityValue) => activityValue !== "undefined" && activityValue !== "null")
                .map((activityValue) => {
                    const activityLabel = activities[activityValue];
                    return {
                        id: String(activityValue),
                        label: activityLabel ? this.$t("label." + activityLabel) : String(activityValue)
                    };
                });
        },
        getGatewaySelectValue: function (optionName, fallbackValue = "") {
            const normalizedFallback = (typeof fallbackValue === "undefined" || fallbackValue === null)
                ? ""
                : String(fallbackValue);

            if (typeof this.selectDraft[optionName] === "undefined" || this.selectDraft[optionName] === null) {
                this.selectDraft[optionName] = normalizedFallback;
            }

            return this.selectDraft[optionName];
        },
        onGatewaySelectChange: function (optionName, value) {
            const normalizedValue = (value && typeof value === "object" && typeof value.id !== "undefined")
                ? value.id
                : value;

            this.selectDraft[optionName] = (typeof normalizedValue === "undefined" || normalizedValue === null)
                ? ""
                : String(normalizedValue);

            this.onSkydropxOptionChange(optionName, this.selectDraft[optionName]);
        },
        getSkydropxLabelKey: function (optionName) {
            return optionName;
        },
        isSkydropxEnabled: function (shippingGateway) {
            const draftValue = this.selectDraft.skydropx_status;
            const fallbackValue = this.getGatewayOptionValue(shippingGateway, "skydropx_status");
            const rawValue = (typeof draftValue === "undefined" || draftValue === null || draftValue === "")
                ? fallbackValue
                : draftValue;

            if (rawValue === true || rawValue === "true") {
                return true;
            }

            const numericValue = parseInt(rawValue, 10);
            return !Number.isNaN(numericValue) && numericValue === 5;
        },
        shouldMarkSkydropxRequired: function (shippingGateway, shippingGatewayOption) {
            return Boolean(shippingGatewayOption?.is_required) && this.isSkydropxEnabled(shippingGateway);
        },
        syncSkydropxFormDrafts: function () {
            const gateway = this.shippingGateways.find((item) => item.slug === "skydropx");
            if (!gateway) {
                return;
            }

            const sandboxMode = parseInt(this.getGatewayOptionValue(gateway, "skydropx_sandbox_mode"), 10);
            this.skydropxDraft.sandboxMode = Number.isNaN(sandboxMode) ? null : sandboxMode;
            this.skydropxDraft.apiBaseUrlProduction = this.getGatewayOptionValue(gateway, "skydropx_api_base_url_production");
            this.skydropxDraft.apiBaseUrlSandbox = this.getGatewayOptionValue(gateway, "skydropx_api_base_url_sandbox");
        },
        loadSimpleProducts: function () {
            this.$store.dispatch("product/getSimpleProduct").then().catch();
        },
        loadQuoteTestAddresses: function () {
            axios.get("admin/setting/shipping-gateway/quote-test-addresses").then((res) => {
                this.quoteTestAddressOptions = res?.data?.data ?? [];
            }).catch(() => {
                this.quoteTestAddressOptions = [];
            });
        },
        setSkydropxTab: function (tab) {
            this.activeSkydropxTab = tab;
        },
        getSkydropxOptionRenderValue: function (shippingGateway, shippingGatewayOption) {
            if (shippingGatewayOption.option === "skydropx_sandbox_mode" && this.skydropxDraft.sandboxMode !== null) {
                return this.skydropxDraft.sandboxMode;
            }

            if (shippingGatewayOption.option === "skydropx_api_base_url_production") {
                return this.skydropxDraft.apiBaseUrlProduction || this.getGatewayOptionValue(shippingGateway, "skydropx_api_base_url_production");
            }

            if (shippingGatewayOption.option === "skydropx_api_base_url_sandbox") {
                return this.skydropxDraft.apiBaseUrlSandbox || this.getGatewayOptionValue(shippingGateway, "skydropx_api_base_url_sandbox");
            }

            return shippingGatewayOption.value;
        },
        onSkydropxOptionInput: function (optionName, value) {
            if (optionName === "skydropx_api_base_url_production") {
                this.skydropxDraft.apiBaseUrlProduction = value;
            }

            if (optionName === "skydropx_api_base_url_sandbox") {
                this.skydropxDraft.apiBaseUrlSandbox = value;
            }
        },
        onSkydropxOptionChange: function (optionName, value) {
            if (optionName === "skydropx_sandbox_mode") {
                const sandboxMode = parseInt(value, 10);
                this.skydropxDraft.sandboxMode = Number.isNaN(sandboxMode) ? null : sandboxMode;
            }
        },
        getSkydropxHiddenValue: function (shippingGateway, optionName) {
            if (optionName === "skydropx_api_base_url_production") {
                return this.skydropxDraft.apiBaseUrlProduction || this.getGatewayOptionValue(shippingGateway, optionName);
            }

            if (optionName === "skydropx_api_base_url_sandbox") {
                return this.skydropxDraft.apiBaseUrlSandbox || this.getGatewayOptionValue(shippingGateway, optionName);
            }

            return this.getGatewayOptionValue(shippingGateway, optionName);
        },
        isSkydropxGateway: function (shippingGateway) {
            return shippingGateway.slug === "skydropx";
        },
        normalizeQuoteItemQuantity: function (item) {
            item.quantity = parseInt(item.quantity, 10);
            if (Number.isNaN(item.quantity) || item.quantity < 1) {
                item.quantity = 1;
            }
        },
        toggleQuoteTestProductAccordion: function () {
            this.quoteTestProductAccordionExpanded = !this.quoteTestProductAccordionExpanded;
        },
        addQuoteTestProduct: function () {
            if (this.quoteTest.isAddingProduct) {
                return;
            }

            const productId = parseInt(this.quoteTest.selectedProductId, 10);
            const quantity = parseInt(this.quoteTest.quantity, 10);

            if (Number.isNaN(productId) || productId < 1) {
                alertService.error(this.$t("label.select_one"));
                return;
            }

            this.quoteTest.isAddingProduct = true;
            this.$store.dispatch("product/show", productId).then((res) => {
                const product = res.data.data;
                const existingItem = this.quoteTest.items.find((item) => parseInt(item.product_id, 10) === productId);
                const safeQuantity = Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;

                if (existingItem) {
                    existingItem.quantity += safeQuantity;
                } else {
                    this.quoteTest.items.push({
                        product_id: product.id,
                        name: product.name,
                        sku: product.sku || `SKU-${product.id}`,
                        price: parseFloat(product.price) || 0,
                        quantity: safeQuantity,
                        shipping: {
                            shipping_type: parseInt(product.shipping_type, 10) || 0,
                            weight: parseFloat(product.weight) || 0.1,
                            length: parseFloat(product.length) || 1,
                            width: parseFloat(product.width) || 1,
                            height: parseFloat(product.height) || 1,
                            shipping_cost: parseFloat(product.shipping_cost) || 0,
                            is_product_quantity_multiply: parseInt(product.is_product_quantity_multiply, 10) || 10
                        }
                    });
                }
				console.log("product:", product, "quoteTestItems:", this.quoteTest.items);

                this.quoteTest.result = null;
                this.quoteTest.errorMessage = "";
                this.quoteTest.selectedProductId = null;
                this.quoteTest.quantity = 1;
                this.quoteTestProductAccordionExpanded = false;
            }).catch((err) => {
                alertService.error(err?.response?.data?.message || err.message);
            }).finally(() => {
                this.quoteTest.isAddingProduct = false;
            });
        },
        removeQuoteTestProduct: function (productId) {
            this.quoteTest.items = this.quoteTest.items.filter((item) => parseInt(item.product_id, 10) !== parseInt(productId, 10));
            this.quoteTest.result = null;
        },
            canRunQuoteTest: function () {
                const shippingAddressId = parseInt(this.quoteTest.shippingAddressId, 10);
                const hasAddress = !Number.isNaN(shippingAddressId) && shippingAddressId > 0;
                const hasProducts = Array.isArray(this.quoteTest.items) && this.quoteTest.items.length > 0;

                return hasAddress && hasProducts;
            },
        runQuoteTest: function () {
            if (this.quoteTest.isCalculating) {
                return;
            }

            const shippingAddressId = parseInt(this.quoteTest.shippingAddressId, 10);

            if (Number.isNaN(shippingAddressId) || shippingAddressId < 1) {
                alertService.error(this.$t("label.shipping_address_id_required"));
                return;
            }

            if (!this.quoteTest.items.length) {
                alertService.error(this.$t("label.quote_test_products_required"));
                return;
            }

            const productsPayload = this.quoteTest.items.map((item) => {
				console.log(item);
				const name = String(item.name || "Producto");
				const sku = String(item.sku || `SKU-${item.product_id}`);
                const quantity = parseInt(item.quantity, 10) > 0 ? parseInt(item.quantity, 10) : 1;
                const shipping = item.shipping || {};
                const price = Number.parseFloat(item.price ?? 0);
                const weight = Number.parseFloat(shipping.weight ?? 0);
                const length = Math.ceil(Number.parseFloat(shipping.length ?? 1));
                const width = Math.ceil(Number.parseFloat(shipping.width ?? 1));
                const height = Math.ceil(Number.parseFloat(shipping.height ?? 1));

                return {
                    name: name,
                    sku: sku,
                    quantity: quantity,
                    price: Number.isFinite(price) ? price : 0,
                    weight: Number.isFinite(weight) && weight > 0 ? weight : 0.1,
                    height: Number.isFinite(height) && height > 0 ? height : 1,
                    length: Number.isFinite(length) && length > 0 ? length : 1,
                    width: Number.isFinite(width) && width > 0 ? width : 1,
                };
            });

			console.log(productsPayload);

            this.quoteTest.isCalculating = true;
            this.quoteTest.errorMessage = "";

            axios.post("frontend/shipping-gateway/quote", {
                shipping_id: shippingAddressId,
                products: productsPayload
            }).then((res) => {
                this.quoteTest.result = res.data.data;
            }).catch((err) => {
                this.quoteTest.result = null;
                this.quoteTest.errorMessage = err?.response?.data?.message || this.$t("message.something_wrong");
            }).finally(() => {
                this.quoteTest.isCalculating = false;
            });
        },
        getGatewayOptionValue: function (shippingGateway, optionName) {
            const option = shippingGateway.options.find((item) => item.option === optionName);
            return option ? option.value : "";
        },
        shouldShowSkydropxOption: function (shippingGateway, optionName) {
            const sandboxMode = this.skydropxDraft.sandboxMode !== null
                ? this.skydropxDraft.sandboxMode
                : parseInt(this.getGatewayOptionValue(shippingGateway, "skydropx_sandbox_mode"), 10);
            const isSandboxEnabled = sandboxMode === 5;

            if (optionName === "skydropx_api_base_url_production") {
                return !isSandboxEnabled;
            }

            if (optionName === "skydropx_api_base_url_sandbox") {
                return isSandboxEnabled;
            }

            return true;
        },
        shouldKeepHiddenSkydropxOption: function (shippingGateway, optionName) {
            if (!["skydropx_api_base_url_production", "skydropx_api_base_url_sandbox"].includes(optionName)) {
                return false;
            }

            return !this.shouldShowSkydropxOption(shippingGateway, optionName);
        },
        getSkydropxSectionOptions: function (shippingGateway, section) {
            const sections = {
                api: [
                    "skydropx_status",
                    "skydropx_sandbox_mode",
                    "skydropx_rate_mode",
                    "skydropx_api_base_url_production",
                    "skydropx_api_base_url_sandbox",
                    "skydropx_api_key",
                    "skydropx_api_secret_key",
                    "skydropx_webhook_secret"
                ],
                origin: [
                    "skydropx_origin_name",
                    "skydropx_origin_phone",
                    "skydropx_origin_email",
                    "skydropx_origin_street1",
                    "skydropx_origin_street2",
                    "skydropx_origin_reference",
                    "skydropx_origin_zip_code",
                    "skydropx_origin_colony",
                    "skydropx_origin_municipality",
                    "skydropx_origin_state",
                    "skydropx_origin_country"
                ],
                security: []
            };

            const sectionOptions = sections[section] || [];
            const optionMap = {};
            shippingGateway.options.forEach((option) => {
                optionMap[option.option] = option;
            });

            const options = sectionOptions.map((optionName) => {
                if (optionMap[optionName]) {
                    return optionMap[optionName];
                }

                if (["skydropx_api_key", "skydropx_api_secret_key", "skydropx_webhook_secret"].includes(optionName)) {
                    return {
                        option: optionName,
                        value: "",
                        type: this.enums.inputTypeEnum.TEXT,
                        activities: {}
                    };
                }

                return null;
            }).filter((item) => item !== null);

            return options.filter((item) => this.shouldShowSkydropxOption(shippingGateway, item.option));
        },
        getSkydropxUsedOptions: function () {
            return [
                "skydropx_status",
                "skydropx_sandbox_mode",
                "skydropx_rate_mode",
                "skydropx_api_base_url_production",
                "skydropx_api_base_url_sandbox",
                "skydropx_api_key",
                "skydropx_api_secret_key",
                "skydropx_origin_name",
                "skydropx_origin_phone",
                "skydropx_origin_email",
                "skydropx_origin_street1",
                "skydropx_origin_street2",
                "skydropx_origin_reference",
                "skydropx_origin_zip_code",
                "skydropx_origin_colony",
                "skydropx_origin_municipality",
                "skydropx_origin_state",
                "skydropx_origin_country",
                "skydropx_webhook_secret"
            ];
        },
        getSkydropxRemainingOptions: function (shippingGateway) {
            const usedOptions = this.getSkydropxUsedOptions();
            return shippingGateway.options.filter((item) => !usedOptions.includes(item.option));
        },
        save: function (index) {
            try {
                this.isSaving = true;
                this.onUpdateActive = true;
                const form = document.getElementById("formElem" + index);
                const formDataObj = {};
                [...form.elements].filter((el) => el.tagName !== "BUTTON").forEach((item) => {
                    formDataObj[item.name] = item.value;
                });

                this.$store.dispatch("shippingGateway/save", { form: formDataObj, search: this.search }).then((res) => {
                    alertService.successFlip(res.config.method === "put" ?? 0, this.$t("menu.shipping_gateway"));
                    this.$store.dispatch("shippingGateway/lists", this.search).then(() => {
                        this.initializeSelectDrafts();
                        this.syncSkydropxFormDrafts();
                    }).catch(() => {
                        // Ignore refresh errors after successful save.
                    });
                    this.errors = {};
                    this.isSaving = false;
                    this.onUpdateActive = false;
                }).catch((err) => {
                    this.errors = err.response.data.errors;
                    this.isSaving = false;
                    this.onUpdateActive = false;
                });
            } catch (err) {
                this.isSaving = false;
                this.onUpdateActive = false;
                alertService.error(err);
            }
        },
        selectActive: function (index) {
            this.selectIndex = index;
            this.syncSkydropxFormDrafts();
        }
    }
};
</script>
